import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useSection, useSettings, resolveUrl } from "../lib/useCMS";

const HERO_FALLBACK = {
  overline: "DEFENSEMEN-SPECIFIC DEVELOPMENT",
  headline_top: "Built",
  headline_bottom: "Different",
  headline_outline: "Hockey",
  lead_bold: "Relentless in the pursuit of excellence.",
  description: "Developing defensemen who think the game, embrace responsibility, and perform under pressure, one detail, one rep, one habit at a time.",
  primary_cta_label: "Book a Clinic",
  primary_cta_url: "https://book.squareup.com/classes/krpp68j9ccjtzm/location/N7TC3KA55X2D8/classes",
  secondary_cta_label: "Join the List",
  secondary_cta_anchor: "#newsletter",
  side_label: "EST. Development · Discipline · Details",
  status_label: "Now Booking",
  status_text: "Semi-private defensemen clinics, limited spots per session.",
  image_url: "https://images.pexels.com/photos/6468935/pexels-photo-6468935.jpeg?auto=compress&cs=tinysrgb&w=2000",
};

const STATS_FALLBACK = {
  items: [
    { value: "6:1", label: "Player-to-Coach Ratio" },
    { value: "10+", label: "Years Coach Experience" },
    { value: "100%", label: "Defensemen Focus" },
    { value: "∞", label: "Relentless Pursuit" },
  ],
};

export const Hero = () => {
  const h = useSection("hero", HERO_FALLBACK);
  const s = useSection("stats", STATS_FALLBACK);
  const settings = useSettings({ square_booking_url: HERO_FALLBACK.primary_cta_url });

  const stats = Array.isArray(s.items) && s.items.length ? s.items : STATS_FALLBACK.items;
  const primaryUrl = h.primary_cta_url || settings.square_booking_url || HERO_FALLBACK.primary_cta_url;

  return (
    <section id="home" data-testid="hero-section" className="relative w-full min-h-screen overflow-hidden bg-black flex flex-col">
      <div className="absolute inset-0">
        <img src={resolveUrl(h.image_url)} alt="Defensemen training on the ice during a Built Different Hockey session" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
      </div>

      <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

      <div className="hidden md:flex absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 flex-col items-center gap-3 z-10">
        <span className="h-20 w-px bg-white/30" />
        <span className="text-[10px] tracking-[0.4em] uppercase text-zinc-400 font-bold rotate-180" style={{ writingMode: "vertical-rl" }}>
          {h.side_label}
        </span>
        <span className="h-20 w-px bg-white/30" />
      </div>

      <div className="hidden md:flex absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 flex-col items-end gap-2 z-10 text-right">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-brand-light rounded-full animate-pulse" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-semibold">{h.status_label}</span>
        </div>
        <p className="text-xs text-zinc-500 max-w-[180px]">{h.status_text}</p>
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 lg:px-16 pb-32 md:pb-36 pt-32 md:pt-36 flex-1 flex flex-col justify-center">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="overline" data-testid="hero-overline">
          {h.overline}
        </motion.p>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-display text-white uppercase leading-[0.85] text-[clamp(2.5rem,9vw,9rem)] mt-6 tracking-[-0.02em]" data-testid="hero-headline">
          <span className="block">{h.headline_top}</span>
          <span className="block">
            {h.headline_bottom}{" "}
            <span className="text-outline-red inline">{h.headline_outline}</span>
          </span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }} className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed" data-testid="hero-description">
          <span className="text-white font-semibold">{h.lead_bold}</span> {h.description}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }} className="mt-10 flex flex-wrap items-center gap-4">
          <a href={primaryUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-book-clinic-button" className="btn-primary">
            {h.primary_cta_label} <ArrowUpRight className="h-5 w-5" />
          </a>
          <a href={h.secondary_cta_anchor || "#newsletter"} data-testid="hero-join-list-button" className="btn-ghost">
            {h.secondary_cta_label}
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7 }} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border-t border-b border-white/10" data-testid="hero-stats">
          {stats.map((stat, i) => (
            <div key={`${stat.label}-${i}`} className="bg-black/60 backdrop-blur-sm px-3 md:px-5 py-5 md:py-6">
              <div className="font-display text-4xl md:text-5xl text-white leading-none">{stat.value}</div>
              <div className="mt-2 text-[9px] md:text-xs tracking-[0.15em] md:tracking-[0.2em] uppercase text-zinc-400 font-semibold">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <a href="#ticker" data-testid="hero-scroll-indicator" className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-zinc-400 hover:text-white transition-colors">
        <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">Scroll</span>
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </a>
    </section>
  );
};
